import { useQuery, useReactiveVar } from '@apollo/client';
import { NavigationContainerRefWithCurrent } from '@react-navigation/core';
import FeatureFlagsContext from 'context/FeatureFlagsContext';
import { GET_APPLICATION_OPEN_SCREEN } from 'graphql/queries/getApplicationUpdateInfo';
import { useContext, useEffect, useState } from 'react';
import {
  featureCountryCode,
  onboardingCompleted,
  tutorialCompletion,
} from 'states/persistInStorage';
import { useAuthStore } from 'stores/authStore';
import { useUiStore } from 'stores/uiStore';
import {
  Query,
  QueryReadApplicationOpenScreenV2Args,
} from 'types/generatedSchemaTypes';
import { RootStackParamList } from 'types/navigation';
import {
  emptyQuery,
  getFormattedVersion,
  getShouldAppUpdate,
} from 'utils/commonUtils';
import { isWeb } from 'utils/deviceUtils';
import { openTutorial } from 'utils/tutorialUtils';
import useIsAccountRestricted from './useIsAccountRestricted';

const useOpenScreenOnAppStartUp = (
  navigationReady: boolean,
  navigationRef: NavigationContainerRefWithCurrent<RootStackParamList>
) => {
  const featureCountry = useReactiveVar(featureCountryCode);
  useIsAccountRestricted(true);
  const isOnboardingCompleted = useReactiveVar(onboardingCompleted);
  const [updateShown, setUpdateShown] = useState(false);
  const {
    flags: { app_version_mandatory, app_version_optional },
  } = useContext(FeatureFlagsContext);
  const completedTutorial = useReactiveVar(tutorialCompletion);

  const {
    data: { readApplicationOpenScreenV2: readApplicationOpenScreen } = {},
  } =
    useQuery<Query, QueryReadApplicationOpenScreenV2Args>(
      GET_APPLICATION_OPEN_SCREEN,
      {
        variables: {
          countryCode: featureCountry,
          completedTutorials: Object.keys(completedTutorial),
        },
        fetchPolicy: 'no-cache',
      }
    ) ?? emptyQuery;

  useEffect(() => {
    if (
      !readApplicationOpenScreen ||
      !navigationReady ||
      !app_version_mandatory ||
      !app_version_optional
    ) {
      return;
    }
    const { hasFailedSession, approveTerms, tutorialId } =
      readApplicationOpenScreen;

    const navigateToUpdateScreen = (isMandatory: boolean) => {
      useUiStore.getState().setUpdateInformation({
        isMandatory,
        openFailedSessions: hasFailedSession,
      });
      useUiStore.getState().setIsAppStartupScreenOpen(true);
      navigationRef.navigate('updateAvailable');
    };

    const navigateToApproveUpdatedTerms = () => {
      useUiStore.getState().setIsAppStartupScreenOpen(true);
      navigationRef.navigate('approveUpdatedTerms');
    };

    const { mandatory, optional } = getShouldAppUpdate(
      app_version_mandatory,
      app_version_optional,
      getFormattedVersion()
    );
    if (!isWeb && (mandatory || optional) && !updateShown) {
      navigateToUpdateScreen(mandatory);
      setUpdateShown(true);
    } else if (approveTerms) {
      //Navigation on web is not actually ready so we need to add timeout
      setTimeout(() => navigateToApproveUpdatedTerms(), isWeb ? 2000 : 0);
    } else if (hasFailedSession) {
      useAuthStore.getState().setAccountRestricted(true);
    } else if (tutorialId && isOnboardingCompleted) {
      setTimeout(() => openTutorial(tutorialId), 5000);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    readApplicationOpenScreen,
    navigationRef,
    navigationReady,
    app_version_mandatory,
    app_version_optional,
    isOnboardingCompleted,
  ]);
};

export default useOpenScreenOnAppStartUp;
